import {NgxMaskModule} from 'ngx-mask';
import {AppInitService} from './services/app-init.service';
import {APP_INITIALIZER, CUSTOM_ELEMENTS_SCHEMA, ErrorHandler, NgModule} from '@angular/core';
import {BrowserModule} from '@angular/platform-browser';
import {RouteReuseStrategy, Router} from '@angular/router';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {IonicModule, IonicRouteStrategy} from '@ionic/angular';
import {AppComponent} from './app.component';
import {AppRoutingModule} from './app-routing.module';
import {TranslateModule, TranslateLoader} from '@ngx-translate/core';
import {TranslateHttpLoader} from '@ngx-translate/http-loader';
import {HttpClientModule, HttpClient, HTTP_INTERCEPTORS} from '@angular/common/http';
import {GlobalService} from './services/global.service';
import {JwtHelperService, JWT_OPTIONS} from '@auth0/angular-jwt';
import {GeneralPopoverModule} from './components/general-popover/general-popover.module';
import {DocumentDetailModalModule} from './pages/kyc/document-detail/document-detail.module';
import {DocumentViewerModalModule} from './components/document-viewer/document-viewer.module';
import {InvoiceDetailsModalModule} from './pages/billing/invoice-details/invoice-details.module';
import {SendFeedbackModalModule} from './pages/dashboard/send-feedback/send-feedback.module';
import {HelpModalModule} from './pages/help/help.module';
import {ImportContactModalModule} from './pages/contacts/import-contact/import-contact.module';
import {AddWorkOrderUpdateModalModule} from './pages/work-orders/add-work-order-update/add-work-order-update.module';
import {NotificationModalModule} from './pages/dashboard/notification-modal/notification.module';

import {EmailTransactionReceiptModalModule} from './pages/transactions/email-transaction-receipt/email-transaction-receipt.module';
import {FileProcessingModalModule} from './modals/file-processing-modal/file-processing-modal.module';
import {SidebarModule} from './components/sidebar';
import {TokenInterceptor, LoaderInterceptor} from './utils/interceptors';
import {MAT_FORM_FIELD_DEFAULT_OPTIONS} from '@angular/material/form-field';
import {MatNativeDateModule, MAT_DATE_LOCALE} from '@angular/material/core';
import {TopbarModule} from './components/topbar';
import {ShepherdService} from 'angular-shepherd';
import {provideUserIdleConfig} from 'angular-user-idle';
import {CountdownModalModule} from './components/countdown-modal/countdown-modal.module';
import * as Sentry from '@sentry/angular-ivy';
import {MatDatepickerModule} from '@angular/material/datepicker';
import {ComponentsModule} from './components/components.module';

// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
export function createTranslateLoader(http: HttpClient) {
    return new TranslateHttpLoader(http, './assets/lang/', '.json');
}

@NgModule({
    declarations: [AppComponent],
    imports: [
        BrowserModule,
        HttpClientModule,
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: createTranslateLoader,
                deps: [HttpClient],
            },
        }),
        IonicModule.forRoot({mode: 'md'}),
        AppRoutingModule,
        GeneralPopoverModule,
        DocumentDetailModalModule,
        DocumentViewerModalModule,
        InvoiceDetailsModalModule,
        SendFeedbackModalModule,
        HelpModalModule,
        ImportContactModalModule,
        AddWorkOrderUpdateModalModule,
        NotificationModalModule,
        EmailTransactionReceiptModalModule,
        FileProcessingModalModule,
        SidebarModule,
        TopbarModule,
        NgxMaskModule.forRoot(),
        BrowserAnimationsModule,
        MatNativeDateModule,
        CountdownModalModule,
        MatDatepickerModule,
        ComponentsModule,
    ],
    providers: [
        GlobalService,
        JwtHelperService,
        {provide: JWT_OPTIONS, useValue: JWT_OPTIONS},
        {provide: RouteReuseStrategy, useClass: IonicRouteStrategy},
        {provide: HTTP_INTERCEPTORS, useClass: TokenInterceptor, multi: true},
        {provide: HTTP_INTERCEPTORS, useClass: LoaderInterceptor, multi: true},
        {provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: {appearance: 'outline'}},
        {provide: MAT_DATE_LOCALE, useValue: 'en-GB'},
        ShepherdService,
        // 44 minutes and 28 seconds is equivalent to 2668 seconds,   the remaining 32 seconds are for timout [30 Timer + 2 for opening modal]
        provideUserIdleConfig({idle: 2668, timeout: 32, ping: 0}),
        {
            provide: ErrorHandler,
            useValue: Sentry.createErrorHandler({
                showDialog: false,
            }),
        },
        {
            provide: Sentry.TraceService,
            deps: [Router],
        },
        {
            provide: APP_INITIALIZER,
            useFactory: () => () => {},
            deps: [Sentry.TraceService],
            multi: true,
        },
    ],
    bootstrap: [AppComponent],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppModule {
    // a little bit hack to initialize the appInitService to load the core data after user is logged in
    constructor(public appInitService: AppInitService) {}
}
